import { Box, type BoxProps, Skeleton, Stack } from "@mui/material";
import {
    type GetContractsResponse,
    type GetLifeDetailResponse,
    PensionPhaseStatus,
    ProductType,
} from "@repo/api";
import { BodyTextL, BodyTextM, BodyTextS, cs, HeadingM, useIsDesktop } from "@repo/ui";
import { type FC } from "react";
import { NumberToCZK } from "../../../functions";
import {
    useContractById,
    useLifeDetail,
    usePensionDetail,
} from "../../../queryHooks";
import Attention from "../../AttentionBlock";
import { DetailLink } from "../ContractCardDesktopMenu";
import {
    CarretButton,
    CarretContainer,
    ContractCardContainer,
    ContractCardSkeletonRect,
    ContractHeaderTitle,
    ContractNumberText,
    MobileDetailLinkBox,
    SkeletonHeaderContractNumber,
    SkeletonHeaderSavings,
    SkeletonHeaderTitle,
} from "./ContractCard.styled";
import {
    formatContractListSubtitle,
    getContractListHeading,
    getContractTypeToDescription,
    getContractTypeToProductName,
    getContractTypeToTitle,
    isChangeInvestmentStrategyInProgress
} from "../Utils/contractUtils";
import LifeCardDescription from "./LifeCardDescription";
import { useAllowedChanges } from "@repo/logic";

interface ContractCardProps {
    readonly contractId: string;
    readonly position?: string;
    readonly type: ProductType;
}

const ContractCard: FC<ContractCardProps> = ({
    contractId,
    position,
    type,
}) => {
    const isDesktop = useIsDesktop();
    const { data: contract } = useContractById({
        id: contractId,
        options: { refetchOnMount: false },
    });
    const { data: allowedChanges, isLoading: allowedChangesLoading } = useAllowedChanges({
        id: contractId,
        options: {
            enabled: contract?.type === ProductType.Uf && !!contractId,
        },
    });
    const { data: starpensionData, isLoading: starpensionDataLoading, isError: starpensionError } = usePensionDetail({
        id: contractId, options: { enabled: type === ProductType.Uf || type === ProductType.Pf }
    });
    const savedAmount = starpensionData?.savedAmount ?? 0;
    const isFufi = contract?.type === ProductType.Clf;

    return (
        <ContractCardContainer
            data-test="contract"
            isPreview={position === "preview"}
            isPreviewHidden={!isDesktop && position === "preview"}
        >
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <HeadingM component={ContractHeaderTitle}>
                    {contract ? <ContractName contract={contract} isDesktop={isDesktop} contractId={contractId} /> : <Skeleton sx={{ mr: 1 }} />}
                </HeadingM>
            </Stack>
            <Stack>
                <BodyTextS light component={ContractNumberText}>
                    <ContractNumber contract={contract} isDesktop={isDesktop} isFufi={isFufi} contractId={contractId} />
                </BodyTextS>
            </Stack>
            {contract && contract.type === ProductType.Cl
                ? <LifeContract contract={contract} contractId={contractId} isDesktop={isDesktop} />
                : <PensionContract
                    contract={contract}
                    starpensionError={starpensionError}
                    starpensionDataLoading={starpensionDataLoading}
                    allowedChanges={allowedChanges}
                    allowedChangesLoading={allowedChangesLoading}
                    savedAmount={savedAmount}
                    type={type}
                />}
            {!isDesktop && (
                <MobileDetailLinkBox>
                    <DetailLink contract={contract} />
                </MobileDetailLinkBox>
            )}
        </ContractCardContainer>
    );
}

const LifeProductName: FC<{ readonly contractId: string }> = ({ contractId }) => {
    const { data, isSuccess } = useLifeDetail<GetLifeDetailResponse>({
        id: contractId,
    });
    if (isSuccess) {
        return <>{data.productName}</>;
    }
    return <Skeleton width={250} />;
};

interface ContractNameProps {
    readonly contract: GetContractsResponse;
    readonly isDesktop: boolean;
    readonly contractId: string;
}

const ContractName: FC<ContractNameProps> = ({ contract, isDesktop, contractId }) => {
    if (contract.type === ProductType.Clf) return <>{contract.title}</>;
    if (isDesktop) {
        return contract.type === ProductType.Cl
            ? <LifeProductName contractId={contractId} />
            : <>{getContractTypeToProductName(contract.type)}</>;
    }
    return <>{getContractListHeading(contract.type, contract.isChildrenContract, contract.childName)}</>;
};

interface ContractNumberProps {
    readonly contract?: GetContractsResponse;
    readonly isDesktop: boolean;
    readonly isFufi: boolean;
    readonly contractId: string;
}

const ContractNumber: FC<ContractNumberProps> = ({ contract, isDesktop, isFufi, contractId }) => {
    if (!contract) return <Skeleton sx={{ mr: 1 }} />;
    if (isDesktop || isFufi) return <>{cs.global.contractNumber} {contractId}</>;

    if (contract.type === ProductType.Cl) {
        return (
            <>
                <LifeProductName contractId={contractId} /> {`(${contractId})`}
            </>
        );
    }

    return (
        <>
            {formatContractListSubtitle(getContractTypeToProductName(contract.type), contractId)}
        </>
    );
};

interface LifeContractProps {
    readonly contract?: GetContractsResponse;
    readonly contractId: string;
    readonly isDesktop: boolean;
}

const LifeContract: FC<LifeContractProps> = ({ contract, contractId, isDesktop }) => {
    return (
        <>
            <LifeCardDescription contractId={contractId} />
            <Stack
                direction={isDesktop ? "column" : "row"}
                justifyContent="space-between"
                alignItems={isDesktop ? "flex-start" : "center"}
            >
                <BodyTextS mb={0} light>
                    {contract ? getContractTypeToDescription(contract.type) : <Skeleton width={150} />}
                </BodyTextS>
                <BodyTextL>
                    {contract ? NumberToCZK(contract.amount) : <Skeleton width={150} />}{" "}
                    <LifeCardDescription contractId={contractId} frequency />
                </BodyTextL>
            </Stack>
        </>
    );
};

interface PensionContractProps {
    readonly contract?: GetContractsResponse;
    readonly starpensionError: boolean;
    readonly starpensionDataLoading: boolean;
    readonly allowedChanges?: { canChangeInvestmentStrategy?: boolean | null };
    readonly allowedChangesLoading: boolean;
    readonly savedAmount: number;
    readonly type: ProductType;
}

const PensionContract: FC<PensionContractProps> = ({
    contract,
    starpensionError,
    starpensionDataLoading,
    allowedChanges,
    allowedChangesLoading,
    savedAmount,
    type,
}) => {
    if (contract?.pensionPhaseStatus === PensionPhaseStatus.Payout) {
        return <BodyTextM light>{cs.global.terminatedContract}</BodyTextM>;
    }
    return (
        <>
            {!starpensionError && (
                <BodyTextS mb={0} light>
                    {contract
                        ? getContractTypeToDescription(contract.type)
                        : <Skeleton />}
                </BodyTextS>
            )}
            <TotalSavings
                contract={contract}
                starpensionError={starpensionError}
                starpensionDataLoading={starpensionDataLoading}
                allowedChanges={allowedChanges}
                allowedChangesLoading={allowedChangesLoading}
                savedAmount={savedAmount}
                type={type}
            />
        </>
    );
};

interface TotalSavingsProps {
    readonly contract?: GetContractsResponse;
    readonly starpensionError: boolean;
    readonly starpensionDataLoading: boolean;
    readonly allowedChanges?: { canChangeInvestmentStrategy?: boolean | null };
    readonly allowedChangesLoading: boolean;
    readonly savedAmount: number;
    readonly type: ProductType;
}

const TotalSavings: FC<TotalSavingsProps> = ({
    contract,
    starpensionError,
    starpensionDataLoading,
    allowedChanges,
    allowedChangesLoading,
    savedAmount,
    type,
}) => {
    if (!contract) return <Skeleton width={150} />;
    if (type === ProductType.Uf || type === ProductType.Pf) {
        if (starpensionError) return null;
        if (starpensionDataLoading) return <Skeleton width={150} />;
        if (isChangeInvestmentStrategyInProgress(allowedChanges, allowedChangesLoading, contract.type)) {
            return (
                <Attention severity="info" sx={{ mb: 4 }}>
                    {cs.investmentStrategy.alerts.fundChangeInProgress.description}
                </Attention>
            );
        }
        return <>{NumberToCZK(savedAmount)}</>;
    }
    return <>{NumberToCZK(contract.amount)}</>;
};

export default ContractCard;

export const CarretWrapper: FC<BoxProps> = ({ children, ...rest }) => {
    return (
        <CarretContainer {...rest}>
            <CarretButton>{children}</CarretButton>
        </CarretContainer>
    );
};

export const ContractCardSkeleton: FC<{
    width: number;
}> = ({
    width,
}) => {
        return (
            <ContractCardSkeletonRect width={width} height={115} variant="rectangular" />
        );
    }
export const ContractCardSkeletonHeader: FC = () => {
    return (
        <Stack>
            <SkeletonHeaderTitle />
            <Stack direction="row" spacing={2}>
                <BodyTextS>{cs.global.contractNumber}</BodyTextS>
                <SkeletonHeaderContractNumber />
            </Stack>

            <Box>
                <BodyTextS>{cs.global.savings}</BodyTextS>
                <SkeletonHeaderSavings />
            </Box>
        </Stack>
    );
}